import React, { Component, Fragment } from 'react';
import { localize } from 'react-localize-redux';
import { connect } from 'react-redux';
import styled from 'styled-components';

import StatusTag from 'components/ui/StatusTag';
import { MaintenanceTicketPriority } from 'components/shared/StatusHelper';

import { List, AutoSizer } from 'react-virtualized';

import { toFriendlyDateString, timeFromNow } from 'helpers/Formatter';
import { AccentColor } from 'helpers/AccentColorHelper';
import { deepDiver } from 'helpers/utilities';
import { colors } from 'helpers/ColorHelper';
import { serviceRequestOptions } from 'helpers/VerbiageHelper';

import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';

const DEFAULT_ROW_HEIGHT = 125;

const StyledDiv = styled.div`
  padding: 0;
  height: 20px;
  min-width: 20px;
  display: inline-block;
  margin-left: 10px;
`;

class MaintenanceTicketsMobile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      rowData: {},
    };
  }

  handleClick = (event, rowData) => {
    if (this.props.disableTableStatusChange) {
      return this.props.goToTicket(rowData.id);
    }

    this.setState({ anchorEl: event.currentTarget, rowData: rowData });
  };

  rowHeight = (index, tableData) => {
    if (tableData[index].tableHeader) return 35;

    if (deepDiver(tableData[index], 'scheduled_at') && deepDiver(tableData[index], 'unit_number')) {
      return 165;
    }

    if (deepDiver(tableData[index], 'scheduled_at') || deepDiver(tableData[index], 'unit_number')) {
      return 155;
    }

    return DEFAULT_ROW_HEIGHT;
  };

  render() {
    const groupedTableDetails = this.props.data;
    return (
      <Fragment>
        <div
          className="mobile_card"
          style={{
            height: this.props.containerHeightOverride || 'calc(100% - 43px)',
            margin: '0 -15px',
          }}
        >
          {this.renderNoOutstandingTickets(groupedTableDetails)}
          {this.renderMobileTicketsVirtualized(groupedTableDetails)}
          {this.renderMobileTicketsList(groupedTableDetails)}
        </div>
      </Fragment>
    );
  }

  renderMobileTicketsList = groupedTableData => {
    if (groupedTableData.length === 0) return;
    if (!this.props.bypassReactVirtualized) return;

    return groupedTableData.map((data, index) => {
      // Passing index in as the key and as the index
      // eslint-disable-next-line
      return this.rowRenderer({ index, index }, groupedTableData);
    });
  };

  renderMobileTicketsVirtualized = groupedTableData => {
    if (groupedTableData.length === 0) return;
    if (this.props.bypassReactVirtualized) return;

    const tableData = groupedTableData;
    return (
      <AutoSizer>
        {({ height, width }) => (
          <List
            rowCount={tableData.length}
            rowHeight={({ index }) => this.rowHeight(index, tableData)}
            rowRenderer={({ key, index, style }, data) =>
              this.rowRenderer({ key, index, style }, tableData)}
            width={width}
            height={height}
          />
        )}
      </AutoSizer>
    );
  };

  renderNoOutstandingTickets = groupedTableData => {
    if (groupedTableData.length !== 0) return;

    const noPendingRequests = {
      serviceRequest: this.props.translate(
        'landlordPortal.maintenanceTicketsMobile.noPendingRequests'
      ),
      requestForMaintenance: this.props.translate(
        'landlordPortal.maintenanceTicketsMobile.noPendingRequestsRFM'
      ),
    };
    return (
      <div id="mobile_card">
        <div className="row">
          <p
            style={{
              textAlign: 'center',
              color: 'rgba(0,0,0,0.5)',
              marginTop: '13px',
            }}
          >
            {' '}
            {serviceRequestOptions(noPendingRequests)}{' '}
          </p>
        </div>
      </div>
    );
  };

  rowRenderer = ({ key, index, style = {} }, data) => {
    const tickets = data;
    let rowStyle = { ...style, padding: '5px 10px' };

    if (['High', 'Emergency'].includes(tickets[index].priority)) {
      rowStyle.borderLeft = `4px solid ${tickets[index].priority === 'Emergency'
        ? colors.RED
        : colors.AMBER}`;
    }

    return (
      <Fragment>
        {this.renderTableHeader(key, rowStyle, tickets[index])}
        {this.renderMobileDetails(key, rowStyle, tickets[index])}
      </Fragment>
    );
  };

  renderTableHeader = (key, rowStyle, rowValues) => {
    if (!rowValues.tableHeader) return;

    return (
      <div key={key} style={rowStyle}>
        {' '}
        <h5
          style={{
            margin: '5px 0 0',
            fontSize: '15px',
            fontFamily: 'Circular-Black',
            color: AccentColor(),
          }}
        >
          {rowValues.tableHeader}
        </h5>{' '}
      </div>
    );
  };

  renderMobileDetails = (key, rowStyle, rowValues) => {
    if (rowValues.tableHeader) return;

    return (
      <div key={key} style={rowStyle}>
        <div className="row">
          <div className="mobile_header">
            {' '}
            <b style={{ fontFamily: 'Circular-Bold' }}>
              {this.props.translate('landlordPortal.maintenanceTicketsMobile.ticketNo', {
                ticketNumber: rowValues.ticket_number,
              })}
            </b>
          </div>
          <div style={{ float: 'right', marginTop: '4px' }}>
            <span style={{ fontSize: '14px', color: 'grey' }}>
              {timeFromNow(rowValues.request_date, {
                language: this.props.currentLanguage,
              })}
            </span>
            <StyledDiv>
              <span>{<StatusTag status={rowValues.status} />}</span>
            </StyledDiv>
          </div>
        </div>

        {this.renderUnitPropertyDetails(rowValues)}
        <div className="row">
          <div className="mobile_subheader">
            <b style={{ fontFamily: 'Circular' }}>
              <MaintenanceTicketPriority priority={rowValues.priority} />
            </b>
          </div>
        </div>
        <div className="row">
          <div className="mobile_subheader">{rowValues.subject}</div>
        </div>
        <RenderIfToggleOn toggle="ui.create_service_request.permission_to_enter">
          <div className="row">
            <div className="mobile_subheader">
              {this.props.translate('landlordPortal.maintenanceTicketsMobile.hasPets')}{' '}
              <b style={{ fontFamily: 'Circular' }}>
                (
                {rowValues.has_pets
                  ? this.props.translate('landlordPortal.maintenanceTicketsMobile.yes')
                  : this.props.translate('landlordPortal.maintenanceTicketsMobile.no')}
                )
              </b>
            </div>
          </div>
          <div className="row">
            <div className="mobile_subheader">
              {this.props.translate('landlordPortal.maintenanceTicketsMobile.entryPermission')}{' '}
              <b style={{ fontFamily: 'Circular' }}>
                (
                {rowValues.permission_to_enter
                  ? this.props.translate('landlordPortal.maintenanceTicketsMobile.yes')
                  : this.props.translate('landlordPortal.maintenanceTicketsMobile.no')}
                )
              </b>
            </div>
          </div>
        </RenderIfToggleOn>
        {this.renderScheduledAtDetails(rowValues)}
        <div className="row">
          <div className="mobile_subheader">
            <button
              type="button"
              className="link-button"
              onClick={() => this.props.goToTicket(rowValues.id)}
              style={{ cursor: 'pointer' }}
            >
              {this.props.translate('landlordPortal.maintenanceTicketsMobile.viewDetails')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderUnitPropertyDetails = rowValues => {
    if (!rowValues.unit_number) return;

    return (
      <div className="row">
        <div className="mobile_subheader">
          {rowValues.unit_number} {rowValues.property_name}
        </div>
      </div>
    );
  };
  renderPropertyDetails = rowValues => {
    if (!rowValues.property_name) return;

    return (
      <div className="row">
        <div className="mobile_subheader">{rowValues.property_name}</div>
      </div>
    );
  };

  renderScheduledAtDetails = rowValues => {
    if (!rowValues.scheduled_at) return;

    return (
      <div className="row">
        <div className="mobile_subheader completion_date">
          {this.props.translate('landlordPortal.maintenanceTicketsMobile.repairScheduledFor')}{' '}
          {toFriendlyDateString(rowValues.scheduled_at, { language: this.props.currentLanguage })}
        </div>
      </div>
    );
  };
}

MaintenanceTicketsMobile = connect(state => ({
  currentLanguage: state.locale.currentLanguage,
}))(MaintenanceTicketsMobile);

export default localize()(MaintenanceTicketsMobile);
